import React from 'react';
import { View, Text, TouchableOpacity, Image, Linking, StyleSheet } from 'react-native';
import { useChatStyles, useChatColors } from './chatStyles';
import { formatTime } from './chatUtils';
import { UserAvatar } from './UserAvatar';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { MessageFile, ReplyToInfo } from '@/types/residents/marketplaceListing';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

const ThreeDotsIcon = RESIDENTS_SVG_COMPONENTS.THREE_DOTS;
const PaperclipIcon = ADMIN_SVG_COMPONENTS.PAPERCLIP;

interface BaseMessageProps {
  text: string;
  timestamp: Date | string;
  onMenuPress?: () => void;
  files?: MessageFile[];
  replyTo?: ReplyToInfo;
}

interface SentMessageBubbleProps extends BaseMessageProps {}

interface ReceivedMessageBubbleProps extends BaseMessageProps {
  userName: string;
  userInitials: string;
}

// Helper to check if a file is an image
const isImageFile = (fileName: string): boolean => {
  const ext = fileName.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

// File attachments renderer
const FileAttachments: React.FC<{ files?: MessageFile[]; isSent?: boolean }> = ({ files, isSent = false }) => {
  const fileStyles = useThemedStyles(makeFileStyles);
  const chatColors = useChatColors();
  if (!files || files.length === 0) return null;

  const imageFiles = files.filter((f) => isImageFile(f.name));
  const documentFiles = files.filter((f) => !isImageFile(f.name));

  return (
    <View style={fileStyles.container}>
      {imageFiles.length > 0 && (
        <View style={fileStyles.imagesRow}>
          {imageFiles.map((file) => (
            <TouchableOpacity
              key={file.id}
              onPress={() => Linking.openURL(file.url)}
              style={fileStyles.imageTouchable}
            >
              <Image
                source={{ uri: file.url }}
                style={fileStyles.imageThumb}
                resizeMode="cover"
              />
            </TouchableOpacity>
          ))}
        </View>
      )}
      {documentFiles.map((file) => (
        <TouchableOpacity
          key={file.id}
          onPress={() => Linking.openURL(file.url)}
          style={fileStyles.documentRow}
        >
          <PaperclipIcon width={14} height={14} color={isSent ? 'rgba(255,255,255,0.7)' : chatColors.textMuted} />
          <Text 
            style={[fileStyles.documentName, isSent && fileStyles.documentNameSent]} 
            numberOfLines={1}
          >
            {file.name}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

const makeFileStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    marginTop: 8,
  },
  imagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
  },
  imageTouchable: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  imageThumb: {
    width: 120,
    height: 120,
    borderRadius: 8,
    backgroundColor: colors.bg.base,
  },
  documentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    gap: 6,
  },
  documentName: {
    fontSize: 13,
    color: semantic.text.primary,
    flex: 1,
  },
  documentNameSent: {
    color: 'rgba(255,255,255,0.9)',
  },
});

// Inline reply preview shown inside a message bubble
const InlineReplyPreview: React.FC<{ replyTo: ReplyToInfo; isSent?: boolean }> = ({ replyTo, isSent = false }) => {
  const chatColors = useChatColors();

  const truncatedText = replyTo.text.length > 60
    ? `${replyTo.text.substring(0, 60)}...`
    : replyTo.text;

  return (
    <View style={[
      replyStyles.container,
      isSent
        ? replyStyles.containerSent
        : { borderLeftColor: chatColors.accentBlueDark, backgroundColor: `${chatColors.accentBlueDark}1A` },
    ]}>
      <Text
        style={[replyStyles.senderName, { color: isSent ? 'rgba(255, 255, 255, 0.9)' : chatColors.accentBlueDark }]}
        numberOfLines={1}
      >
        {replyTo.senderName}
      </Text>
      <Text
        style={[replyStyles.messageText, { color: isSent ? 'rgba(255, 255, 255, 0.7)' : chatColors.textSecondary }]}
        numberOfLines={2}
      >
        {truncatedText || '(Image)'}
      </Text>
    </View>
  );
};

const replyStyles = StyleSheet.create({
  container: {
    borderLeftWidth: 2,
    paddingLeft: 8,
    paddingVertical: 4,
    marginBottom: 6,
    borderRadius: 2,
  },
  containerSent: {
    borderLeftColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  senderName: {
    fontSize: 11,
    fontWeight: '600',
    lineHeight: 14,
    marginBottom: 2,
  },
  messageText: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
});

/**
 * Sent message bubble (User's messages - right aligned, blue)
 */
export const SentMessageBubble: React.FC<SentMessageBubbleProps> = ({ 
  text, 
  timestamp,
  onMenuPress,
  files,
  replyTo,
}) => {
  const chatStyles = useChatStyles();

  return (
  <View style={chatStyles.sentMessageContainer}>
    <View style={chatStyles.sentMessageBubble}>
      <View style={chatStyles.messageContent}>
        <View style={chatStyles.messageHeader}>
          <Text style={chatStyles.sentMessageLabel}>You</Text>
          {onMenuPress && (
            <TouchableOpacity
              style={chatStyles.menuButton}
              onPress={onMenuPress}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <ThreeDotsIcon color="rgba(238, 241, 244, 0.7)" />
            </TouchableOpacity>
          )}
        </View>
        {replyTo && <InlineReplyPreview replyTo={replyTo} isSent />}
        {text ? <Text style={chatStyles.sentMessageText}>{text}</Text> : null}
        <FileAttachments files={files} isSent />
      </View>
      <View style={chatStyles.timestampContainer}>
        <Text style={chatStyles.timestampText}>{formatTime(timestamp)}</Text>
      </View>
    </View>
  </View>
  );
};

/**
 * Received message bubble (Other user's messages - left aligned, gray)
 */
export const ReceivedMessageBubble: React.FC<ReceivedMessageBubbleProps> = ({ 
  text, 
  timestamp,
  userName, 
  userInitials,
  onMenuPress,
  files,
  replyTo,
}) => {
  const chatStyles = useChatStyles();
  const chatColors = useChatColors();

  return (
  <View style={chatStyles.receivedMessageContainer}>
    <UserAvatar initials={userInitials} size="small" />
    <View style={chatStyles.receivedMessageBubble}>
      <View style={chatStyles.messageContent}>
        <View style={chatStyles.messageHeader}>
          <Text style={chatStyles.receivedMessageLabel}>{userName}</Text>
          {onMenuPress && (
            <TouchableOpacity
              style={chatStyles.menuButton}
              onPress={onMenuPress}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <ThreeDotsIcon color={chatColors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        {replyTo && <InlineReplyPreview replyTo={replyTo} />}
        {text ? <Text style={chatStyles.receivedMessageText}>{text}</Text> : null}
        <FileAttachments files={files} />
      </View>
      <View style={chatStyles.timestampContainer}>
        <Text style={chatStyles.timestampText}>{formatTime(timestamp)}</Text>
      </View>
    </View>
  </View>
  );
};
